import React, { useState } from "react";
import {
  Backdrop,
  Box,
  CircularProgress,
  Typography,
  Stack,
  Fade,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "react-query";
import API from "../../../API";
import { globalStyles } from "../../../Utils/Styles";
import MarketplaceBooking from "./MarketplaceBooking";
import AlertDialog from "../../../Components/AlertDialog";
import useAlertDialog from "../../../Components/useAlertDialog";
import {
  parseLocalFloat,
  sortCreators,
  getPlatformAndPromotion,
  platformToFollowing,
  platformToLink,
} from "../../../Utils/constants";
import routes from "../../../Config/routes";

const SearchPage = () => {
  const navigate = useNavigate();
  // Alerts & backdrops
  const { dialogState, openDialog, closeDialog } = useAlertDialog();
  const [searchParams] = useSearchParams();
  const clientEmailPrefill = searchParams.get('client') || '';

  // Fetch creators list
  // Update the query to include ranking metadata
const {
  data: creatorList,
  isFetching: creatorIsLoading,
} = useQuery({
  queryKey: ["creators", "v2"],
  queryFn: async () => {
    console.log('🔍 Fetching creators from API...');
    const result = await API.creators.list({ 
      include_reviews: true, 
      include_public: true,
      include_ranking: true  // ← Add this
    });
    console.log('✅ API Response:', result);
    console.log('📊 Creator count:', result?.length);
    return sortCreators(result);
  },
  refetchOnWindowFocus: false,
  initialData: [],
  cacheTime: 0,
  staleTime: 0,
  refetchOnMount: true,
});

  // Mutation: create multi-influencer campaign
  const { mutate: postCampaign, isLoading: loadingCampaign } = useMutation({
    mutationFn: API.campaigns.create,
    onError: (error) => {
      openDialog(
        "Campaign Creation Failed",
        <>
          <Typography variant="body1" sx={{ mb: 2 }}>
            We encountered an error while creating your campaign:
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              p: 2, 
              backgroundColor: 'error.50', 
              borderRadius: 2,
              fontFamily: 'monospace',
              color: 'error.main'
            }}
          >
            {error?.response?.data?.error || error.message}
          </Typography>
        </>,
        closeDialog,
        closeDialog,
        "Try Again"
      );
    },
    onSuccess: (data) => {
      openDialog(
        "Campaign Created Successfully!",
        <>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Your campaign has been created and is ready for review.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You can now manage creators, timelines, and campaign details.
          </Typography>
        </>,
        () => navigate(`/editcampaign/${data.id}`),
        () => navigate(`/editcampaign/${data.id}`),
        "View Campaign"
      );
    },
  });

  // Mutation: create single-partnership
  const { mutate: postPartnership, isLoading: loadingPartnership } = useMutation(
    API.partnerships.book,
    {
      onError: (error) => {
        openDialog(
          "Partnership Creation Failed",
          <>
            <Typography variant="body1" sx={{ mb: 2 }}>
              We encountered an error while creating your partnership:
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                p: 2, 
                backgroundColor: 'error.50', 
                borderRadius: 2,
                fontFamily: 'monospace',
                color: 'error.main'
              }}
            >
              {error?.response?.data?.error || error.message}
            </Typography>
          </>,
          closeDialog,
          closeDialog,
          "Try Again"
        );
      },
      onSuccess: (data) => {
        openDialog(
          "Partnership Created Successfully!",
          <>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Your partnership has been created and is ready for review.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You can now manage the partnership details and timeline.
            </Typography>
          </>,
          () => navigate(`/partnerships/${data.data.id}`),
          () => navigate(`/partnerships/${data.data.id}`),
          "View Partnership"
        );
      },
    }
  );

  /**
   * Called by MarketplaceBooking on the final step for multi-creator flows
   */
  const onFinishCampaign = (campaign) => {
    const newCreators = campaign.creatorsData.map((creator) => {
      const price = parseFloat(creator.adjustedPrice);
      const getNextId = (videos) => {
        const highestId = videos.reduce((max, video) => Math.max(max, video.id), 0);
        return highestId + 1;
      };
      const nextId = getNextId(creator.videos);
      let crossPostOffset = 0; // Track the cumulative cross-post offset
      return {
        id: creator.creator,
        name: creator.creator,
        price,
        agencyRate: price * 0.90,
        pfphref: creator.pfphref,
        videos: [
          ...creator.videos.map((video) => {
            const { platform, promotion } = getPlatformAndPromotion(video.rateKey);
            return ({
              id: video.id,
              parentId: null,
              promotionPlatform: platform,
              promotionType: promotion,
              platformLink: creator[platformToLink[platform]],
              following: creator[platformToFollowing[platform]],
            })
          }),
          ...creator.videos.flatMap((video) => {
            const result = [...video.crossPostingKeys].map((crossPost, index) => {
              const { platform, promotion } = getPlatformAndPromotion(crossPost);
              return ({
                id: nextId + crossPostOffset + index,
                parentId: video.id,
                promotionPlatform: platform,
                promotionType: promotion,
                platformLink: creator[platformToLink[platform]],
                following: creator[platformToFollowing[platform]],
              })
            })
            crossPostOffset += video.crossPostingKeys.size;
            return result;
          }
          )
        ]
      };
    });

    const timelineEvents = Object.entries(campaign.timelinesData).flatMap(
      ([creatorId, events]) => events.map((event) => ({ ...event, username: creatorId }))
    );

    const campaignPayload = {
      name: campaign.campaignData.campaignName,
      brief: campaign.campaignData.asset
        ? campaign.campaignData.asset.file_url
        : campaign.campaignData.campaignBrief,
      creators: newCreators,
      timelines: timelineEvents,
      idealDueDate: campaign.campaignData.idealDueDate,
      emailRecipient: campaign.campaignData.emailRecipient,
      agency: campaign.campaignData.isAgency,
      campaign_manager: campaign.campaignData.manager,
    };

    // Normal multi-creator campaign creation
    postCampaign(campaignPayload);
  };

  /**
   * Called if user clicks "Invite to partnership" from the LeftColumnSearch (optional).
   * Or we can rely solely on the cart logic.
   */
  const handleInviteToPartnership = (creator) => {
    // Navigate to the internal booking page, passing the creator data via state
    navigate(routes.creatorBooking, { state: { creator } });
  };

  const isLoading = loadingCampaign || loadingPartnership;

  return (
    <>
      <AlertDialog alertState={dialogState} />

      {/* MODERN LOADING BACKDROP */}
      <Backdrop
        sx={{ 
          color: "#fff", 
          zIndex: (theme) => theme.zIndex.modal + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)'
        }}
        open={isLoading}
      >
        <Fade in={isLoading}>
          <Stack alignItems="center" spacing={3}>
            <CircularProgress 
              size={60} 
              thickness={4}
              sx={{
                color: 'primary.main',
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round',
                }
              }}
            />
            <Stack alignItems="center" spacing={1}>
              <Typography variant="h6" fontWeight={600}>
                {loadingCampaign && "Creating Campaign..."}
                {loadingPartnership && "Creating Partnership..."}
              </Typography>
              <Typography variant="body2" color="rgba(255, 255, 255, 0.8)">
                {loadingCampaign && "Setting up your multi-creator campaign"}
                {loadingPartnership && "Establishing your creator partnership"}
              </Typography>
            </Stack>
          </Stack>
        </Fade>
      </Backdrop>

      {/* MAIN CONTAINER WITH MODERN STYLING */}
      <Box
        id="search-holder"
        sx={{
          ...globalStyles.wholeHeightMinusToolbar,
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          bgcolor: 'background.default',
          background: (theme) => theme.palette.mode === 'dark'
            ? `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`
            : 'linear-gradient(135deg, #fafafa 0%, #f0f0f0 100%)',
          minHeight: '100vh'
        }}
      >
        <MarketplaceBooking
          onInviteToPartnership={handleInviteToPartnership}
          data={creatorList}
          loading={creatorIsLoading}
          onSubmitCampaign={onFinishCampaign}
          prefilterEmail={clientEmailPrefill}
        />
      </Box>
    </>
  );
};

export default SearchPage;